import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useHistory, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/GetApp';
import { DataAPIContext, GET_LIST } from '../api';
import { ContextStore } from '../store/contextStore';
import MediaCard from './MediaCard';
import FixedLinearProgress from '../components/FixedLinearProgress';
import AccessDeniedView from '../components/AccessDeniedView';
import PageMeta from '../components/PageMeta';
import config from '../config';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: `calc(${theme.cp.contentHeight} - 3rem)`,
        paddingBottom: '1rem',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 16px',
        height: '72px',
        backgroundColor: '#FCFCFE',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            height: 'auto',
            padding: '12px 16px 16px',
            gap: '12px',
        },
    },
    titleBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '40px',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            gap: '12px',
        },
    },
    backButton: {
        padding: '8px',
    },
    title: {
        fontSize: '20px',
        fontWeight: 500,
        color: '#000000',
        [theme.breakpoints.down('sm')]: {
            fontSize: '18px',
            lineHeight: 1.3,
        },
    },
    contentWrapper: {
        flex: 1,
        padding: '0 16px',
        display: 'flex',
        flexDirection: 'column',
    },
    gridContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        padding: '16px',
        border: '1px solid #D8D8D8',
        borderRadius: '12px',
        alignContent: 'flex-start',
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px',
    },
    downloadButton: {
        textTransform: 'uppercase',
        color: '#007AFF',
        borderColor: '#007AFF',
        fontSize: '14px',
        fontWeight: 500,
        letterSpacing: '0.07em',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
}));

const TourMediaDetailPanel = () => {
    const classes = useStyles();
    const history = useHistory();
    const { tourId } = useParams();
    const dataAPI = useContext(DataAPIContext);
    const { token } = useContext(ContextStore);

    const [loading, setLoading] = useState(false);
    const [tourName, setTourName] = useState(null);
    const [mediaFiles, setMediaFiles] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMedia, setSelectedMedia] = useState([]);
    const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const loadMoreRef = useRef(null);
    const [showNoMore, setShowNoMore] = useState(false);
    const noMoreTimeoutRef = useRef(null);
    const hasScrolledRef = useRef(false);

    useEffect(() => {
        setTourName(null);
        setMediaFiles([]);
        setPage(1);
        setHasMore(true);
        hasScrolledRef.current = false;
        fetchMediaFiles(1, true);
    }, [tourId]);

    useEffect(() => {
        const handleScroll = () => {
            hasScrolledRef.current = true;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    if (!loading && hasMore) {
                        fetchMediaFiles(page + 1, false);
                    } else if (!hasMore && mediaFiles.length > 0 && hasScrolledRef.current) {
                        if (noMoreTimeoutRef.current) {
                            clearTimeout(noMoreTimeoutRef.current);
                        }
                        setShowNoMore(true);
                        noMoreTimeoutRef.current = setTimeout(() => {
                            setShowNoMore(false);
                        }, 3000);
                    }
                }
            },
            { threshold: 0.1 }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => {
            if (loadMoreRef.current) {
                observer.unobserve(loadMoreRef.current);
            }
            if (noMoreTimeoutRef.current) {
                clearTimeout(noMoreTimeoutRef.current);
            }
        };
    }, [loading, hasMore, page, mediaFiles.length]);



    const fetchMediaFiles = async (pageToLoad = page, reset = false) => {
        if (!reset && !hasMore) return;

        setLoading(true);
        setError(null);
        try {
            const response = await dataAPI(GET_LIST, `tour/${tourId}/media`, {
                pagination: { page: pageToLoad, perPage: 25 },
                filter: {},
            });

            if (reset) {
                setMediaFiles(response.data || []);
                // Get tour name from first media item
                if (response.data?.[0]?.tour_name) {
                    setTourName(response.data[0].tour_name);
                }
            } else {
                setMediaFiles(prev => [...prev, ...(response.data || [])]);
            }
            setHasMore((response.data || []).length === 25);
            setPage(pageToLoad);
        } catch (error) {
            console.error('Error fetching media files:', error);
            setError('Failed to load media files. Please try again.');
            if (reset) {
                setMediaFiles([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        history.push('/marketing-suite/media');
    };

    const handleMediaSelect = (mediaId, index, event) => {
        if (event?.shiftKey && lastSelectedIndex !== null) {
            const start = Math.min(lastSelectedIndex, index);
            const end = Math.max(lastSelectedIndex, index);
            const rangeIds = mediaFiles.slice(start, end + 1).map(m => m.id);

            setSelectedMedia((prev) => {
                const newSelected = new Set(prev);
                rangeIds.forEach(id => newSelected.add(id));
                return Array.from(newSelected);
            });
        } else {
            setSelectedMedia((prev) =>
                prev.includes(mediaId)
                    ? prev.filter((id) => id !== mediaId)
                    : [...prev, mediaId]
            );
        }
        setLastSelectedIndex(index);
    };

    const handleSelectAll = () => {
        setSelectedMedia(mediaFiles.map(m => m.id));
    };

    const handleSelectNone = () => {
        setSelectedMedia([]);
    };

    const handleDownloadSelected = async () => {
        if (downloading) return;

        setDownloading(true);
        const mediaIds = selectedMedia.join(',');
        const url = `${config.apiUrl}/tour/media/download/?media_ids=${mediaIds}`;

        try {
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Token ${token}`,
                },
            });

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `${tourName || 'tour'}_media_${new Date().toISOString().split('T')[0]}.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading ZIP:', error);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box className={classes.root}>
            <PageMeta title={tourName ? `Marketing Suite - ${tourName}` : 'Marketing Suite'} />
            <FixedLinearProgress visibility={loading && mediaFiles.length === 0} />
            <Box className={classes.header}>
                <Box className={classes.titleBox}>
                    <IconButton onClick={handleBack} className={classes.backButton}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography className={classes.title}>
                        {tourName || 'Loading...'} ({mediaFiles.length}{hasMore ? '+' : ''} files)
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                    {selectedMedia.length < mediaFiles.length && mediaFiles.length > 0 && (
                        <Button
                            variant="text"
                            onClick={handleSelectAll}
                            sx={{
                                textTransform: 'uppercase',
                                color: '#007AFF',
                                fontSize: '14px',
                                fontWeight: 500,
                                letterSpacing: '0.07em',
                            }}
                        >
                            select all
                        </Button>
                    )}
                    {selectedMedia.length > 0 && (
                        <Button
                            variant="text"
                            onClick={handleSelectNone}
                            sx={{
                                textTransform: 'uppercase',
                                color: '#007AFF',
                                fontSize: '14px',
                                fontWeight: 500,
                                letterSpacing: '0.07em',
                            }}
                        >
                            select none
                        </Button>
                    )}
                    {selectedMedia.length > 0 && (
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadSelected}
                            disabled={downloading}
                            className={classes.downloadButton}
                        >
                            {downloading ? 'downloading...' : `download files (${selectedMedia.length})`}
                        </Button>
                    )}
                </Box>
            </Box>

            <Box className={classes.contentWrapper}>
                {error ? (
                    <Box className={classes.gridContainer}>
                        <Typography sx={{ fontSize: '14px', color: '#d32f2f', width: '100%', textAlign: 'center', padding: '40px' }}>
                            {error}
                        </Typography>
                    </Box>
                ) : !loading && mediaFiles.length === 0 ? (
                    <Box className={classes.gridContainer}>
                        <Typography sx={{ fontSize: '14px', color: '#666', width: '100%', textAlign: 'center', padding: '40px' }}>
                            No media files found for this tour
                        </Typography>
                    </Box>
                ) : mediaFiles.length === 0 ? (
                    <Box className={classes.gridContainer} />
                ) : (
                    <Box className={classes.gridContainer}>
                        {mediaFiles.map((media, index) => (
                            <MediaCard
                                key={`media-${media.id}-${index}`}
                                media={media}
                                index={index}
                                selected={selectedMedia.includes(media.id)}
                                onSelect={handleMediaSelect}
                            />
                        ))}
                    </Box>
                )}

                <Box ref={loadMoreRef} sx={{ height: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, mb: 2 }}>
                    {loading && hasMore && mediaFiles.length > 0 && (
                        <Typography variant="body2" color="text.secondary">Loading more...</Typography>
                    )}
                    {showNoMore && (
                        <Typography variant="body2" color="text.secondary">No more files</Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

const TourMediaDetail = () => {
    const { hasPerm } = useContext(ContextStore);
    if (!hasPerm('tour_media', 'read')) {
        return (
            <>
                <PageMeta title="Marketing Suite" />
                <AccessDeniedView />
            </>
        );
    }
    return <TourMediaDetailPanel />;
};

export default TourMediaDetail;
